/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {QCriteriaOperator} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QCriteriaOperator";
import {QQueryFilter} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QQueryFilter";


/***************************************************************************
 ** Build the values to pre-populate into a new record created from a possible
 ** value source's dropdown, from that source's own (already resolved) filter.
 **
 ** Only single-value EQUALS criteria seed a field - those are the only ones with
 ** an unambiguous "the new record must have this value" reading.  Other criteria
 ** shapes still scope the dropdown, so a record created here can fail to satisfy
 ** them; that is caught when the new id is resolved back to a possible value.
 **
 ** Seeded fields are also DISABLED on the create form, so anything seeded that is
 ** not genuinely a requirement over-constrains the new record with no way out.
 ** That is why the reads below are all conservative: only under AND is an EQUALS
 ** criterion a requirement at all, and anything ambiguous seeds nothing.
 ** (subFilters need no such treatment - under AND, the top-level criteria are
 ** requirements no matter what any nested group says.)
 **
 ** Lives in its own module rather than in DynamicSelect - its only caller - purely
 ** so that it stays testable.  jest cannot import DynamicSelect: it reaches
 ** RecordPickerModal, then RecordQuery, then MenuButton, which imports an ESM build
 ** that the CRA jest transform rejects.  Moving this function there would make
 ** dynamicSelectSeedValues.test.ts unrunnable, and the reads below - OR failing
 ** closed, join-qualified names, disagreeing duplicate EQUALS, QPossibleValue
 ** unwrapping - are the kind that want tests.
 ***************************************************************************/
export function buildSeedValuesFromFilter(filter: QQueryFilter): {[key: string]: any}
{
   //////////////////////////////////////////////////////////////////////////////////
   // under OR, an EQUALS criterion is not a requirement - "clientId=47 OR status  //
   // =ACTIVE" is satisfied by a record with neither clientId 47 nor that status.  //
   // Seeding (and disabling) clientId there would be simply wrong, so fail closed //
   // and seed nothing.                                                            //
   //////////////////////////////////////////////////////////////////////////////////
   if (filter?.booleanOperator && filter.booleanOperator !== "AND")
   {
      return ({});
   }

   const seedValues: {[key: string]: any} = {};
   const seenFieldNames = new Set<string>();

   for (let i = 0; i < (filter?.criteria?.length ?? 0); i++)
   {
      const criteria = filter.criteria[i];
      if (criteria.operator == QCriteriaOperator.EQUALS && criteria.values?.length == 1)
      {
         //////////////////////////////////////////////////////////////////////////////
         // a join-qualified name (otherTable.someField) is not a field on the table //
         // being created, so seeding it would put a value under a name the form has //
         // no input for                                                             //
         //////////////////////////////////////////////////////////////////////////////
         if (criteria.fieldName == null || criteria.fieldName.indexOf(".") > -1)
         {
            continue;
         }

         const value = criteria.values[0];

         ////////////////////////////////////////////////////////////////////////////////////
         // EQUALS null says the new record's field must be empty, which is what the form  //
         // already gives it - and seeding it would DISABLE the input, so a required field //
         // could never be filled in and the record could never be saved.  Seed nothing.   //
         ////////////////////////////////////////////////////////////////////////////////////
         if (value == null)
         {
            continue;
         }

         ///////////////////////////////////////////////////////////////////////////////////
         // an EXPRESSION - a date expression like {type: "NowWithOffset"}, or a filter   //
         // variable - is an object with no id.  Seeding it verbatim would put an object  //
         // into a scalar input and then disable it, leaving an un-editable field holding //
         // something the form cannot render.  Ambiguous, so seed nothing, per this       //
         // module's rule above.                                                          //
         ///////////////////////////////////////////////////////////////////////////////////
         if (typeof value === "object" && !("id" in value))
         {
            continue;
         }

         ////////////////////////////////////////////////////////////////////////
         // a possible-value criterion's value may have been cleaned up into a //
         // QPossibleValue object (id + label) - the form wants just the id.   //
         ////////////////////////////////////////////////////////////////////////
         const seedValue = (typeof value === "object" && "id" in value) ? value.id : value;

         //////////////////////////////////////////////////////////////////////////////////
         // two EQUALS on the same field, ANDed, is unsatisfiable unless they agree - so //
         // rather than letting the later one silently win, seed neither                 //
         //////////////////////////////////////////////////////////////////////////////////
         if (seenFieldNames.has(criteria.fieldName))
         {
            if (seedValues[criteria.fieldName] !== seedValue)
            {
               delete seedValues[criteria.fieldName];
            }
            continue;
         }

         seenFieldNames.add(criteria.fieldName);
         seedValues[criteria.fieldName] = seedValue;
      }
   }

   return (seedValues);
}



/*******************************************************************************
 ** The default value for a single-select, built from the id and label a caller
 ** supplied as that select's initial value.
 **
 ** The id is checked for being present rather than for being truthy, because 0
 ** is a legitimate possible-value id - enum ordinals produce it - and treating
 ** it as "no value" renders the control blank even though a preset was
 ** supplied.  An id of "" is a different matter: no record has an empty-string
 ** primary key, so that means no value and is treated as such.
 **
 ** An empty LABEL with a real id is kept - a record may legitimately display
 ** as blank.
 *******************************************************************************/
export function buildSingleSelectDefaultValue(initialValue: any, initialDisplayValue: any): { id: any, label: any }[]
{
   if (initialValue === null || initialValue === undefined || initialValue === "" || initialDisplayValue === null || initialDisplayValue === undefined)
   {
      return (null);
   }

   return ([{id: initialValue, label: initialDisplayValue}]);
}



/*******************************************************************************
 ** The label to render for a possible-value control that a process preset a
 ** value into, or undefined to leave the control alone.
 **
 ** Sources are tried in order of how much they can be trusted:
 **
 ** 1. the field's own inline possible-value list, which arrives on the same
 **    payload as the preset and therefore cannot be stale relative to it;
 ** 2. the module-scope label cache, which is keyed on a bare field name with
 **    no process or table qualifier and is never cleared, so a field named
 **    e.g. "status" can hold a label cached for a different table's "status";
 ** 3. the raw id, as a last resort so the control shows something.
 **
 ** That last resort is skipped for a FALSY id - 0, "", false, NaN.  Those are
 ** legitimate ids when a list actually offers them, which is why the lookups
 ** above must not gate on truthiness and why the caller must not either - but
 ** when nothing recognizes one it almost always means "no value", and showing
 ** it renders a selected-looking option that is not in the control's own list.
 ** Note the string "0" is truthy and so does fall back, same as any other
 ** string id nothing recognizes.
 *******************************************************************************/
export function resolvePresetDisplayValue(presetValue: any, inlinePossibleValues: any[], cachedLabelsForField: {[id: string]: string}): any
{
   if (presetValue === null || presetValue === undefined || presetValue === "")
   {
      return (undefined);
   }

   const inlinePossibleValue = inlinePossibleValues?.find((possibleValue: any) => String(possibleValue.id) === String(presetValue));
   if (inlinePossibleValue)
   {
      return (inlinePossibleValue.label);
   }

   if (cachedLabelsForField && cachedLabelsForField[presetValue])
   {
      return (cachedLabelsForField[presetValue]);
   }

   if (!presetValue)
   {
      return (undefined);
   }

   return (presetValue);
}
